import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { createWorkShift, updateWorkShift } from '../../../services/workforceServices'
import { extractBackendError, invalidateWorkforce } from '../../../utils/workforceUtils'

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const parseTime = (s) => (s || '').slice(0, 5)

export const ShiftFormDialog = ({ shift, warehouse_id, onClose }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const editing = !!shift

    const [open, setOpen] = useState(false)
    const [values, setValues] = useState({
        name: shift?.name || '',
        schedule_days: shift?.schedule_days || [],
        start_time: parseTime(shift?.start_time) || '08:00',
        end_time: parseTime(shift?.end_time) || '16:00',
        shift_period: shift?.shift_period || 'day',
        active: shift?.active ?? true,
    })
    const [baseError, setBaseError] = useState('')

    useEffect(() => { setOpen(true) }, [])

    const close = (didChange = false) => {
        setOpen(false)
        setTimeout(() => onClose(didChange), 300)
    }

    const mutation = useMutation(
        (data) => editing ? updateWorkShift(warehouse_id, shift.id, data) : createWorkShift(warehouse_id, data),
        {
            onSuccess: () => {
                invalidateWorkforce(queryClient, warehouse_id, ['shifts', 'attendance', 'costs', 'costs-range'])
                close(true)
            },
            onError: (err) => {
                const { message } = extractBackendError(err)
                setBaseError(message)
            },
        }
    )

    const toggleDay = (i) => {
        setValues((prev) => ({
            ...prev,
            schedule_days: prev.schedule_days.includes(i)
                ? prev.schedule_days.filter((d) => d !== i)
                : [...prev.schedule_days, i].sort((a, b) => a - b),
        }))
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setBaseError('')
        if (!values.name.trim()) { setBaseError(t('workforce.shifts.errors.name_required')); return }
        if (values.schedule_days.length === 0) { setBaseError(t('workforce.shifts.errors.days_required')); return }
        if (values.shift_period !== 'day' && values.shift_period !== 'night') {
            setBaseError(t('workforce.shifts.errors.period_required')); return
        }
        mutation.mutate({
            name: values.name.trim(),
            schedule_days: values.schedule_days,
            start_time: values.start_time,
            end_time: values.end_time,
            shift_period: values.shift_period,
            active: values.active,
        })
    }

    const overnight = values.end_time < values.start_time

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-20 overflow-y-auto" open={open} onClose={() => close(false)}>
                <div className="flex items-center justify-center min-h-screen px-4">
                    <Dialog.Overlay className="fixed inset-0 bg-gray-700 bg-opacity-40" />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium">{editing ? t('workforce.shifts.edit_title') : t('workforce.shifts.create_title')}</Dialog.Title>
                            <button type="button" onClick={() => close(false)} className="text-gray-400 hover:text-gray-500">
                                <XIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                            {baseError && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{baseError}</div>}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.shifts.name')}</label>
                                <input
                                    type="text"
                                    value={values.name}
                                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                                    className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.shifts.schedule_days')}</label>
                                <div className="flex flex-wrap gap-2">
                                    {DAY_KEYS.map((d, i) => {
                                        const selected = values.schedule_days.includes(i)
                                        return (
                                            <button
                                                key={d}
                                                type="button"
                                                onClick={() => toggleDay(i)}
                                                className={`px-3 py-1.5 rounded-md border text-sm ${selected ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                            >
                                                {t(`workforce.days.${d}`)}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.shifts.start_time')}</label>
                                    <input
                                        type="time"
                                        value={values.start_time}
                                        onChange={(e) => setValues({ ...values, start_time: e.target.value })}
                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.shifts.end_time')}</label>
                                    <input
                                        type="time"
                                        value={values.end_time}
                                        onChange={(e) => setValues({ ...values, end_time: e.target.value })}
                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                    />
                                </div>
                            </div>
                            {overnight && (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                    {t('workforce.shifts.overnight_hint')}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.shifts.shift_period')}</label>
                                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                                    {['day', 'night'].map((p) => {
                                        const selected = values.shift_period === p
                                        return (
                                            <button
                                                key={p}
                                                type="button"
                                                onClick={() => setValues({ ...values, shift_period: p })}
                                                className={`px-4 py-1.5 text-sm font-medium transition-colors ${selected ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                            >
                                                {t(`workforce.shifts.period_${p}`)}
                                            </button>
                                        )
                                    })}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">{t('workforce.shifts.period_hint')}</p>
                            </div>
                            <label className="inline-flex items-center gap-2">
                                <input type="checkbox" checked={values.active} onChange={(e) => setValues({ ...values, active: e.target.checked })} />
                                <span className="text-sm text-gray-700">{t('workforce.shifts.is_active')}</span>
                            </label>
                        </form>
                        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-2">
                            <button type="button" onClick={() => close(false)} disabled={mutation.isLoading}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                                {t('workforce.cancel')}
                            </button>
                            <button type="button" onClick={handleSubmit} disabled={mutation.isLoading}
                                className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                                {mutation.isLoading ? t('workforce.saving') : t('workforce.save')}
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
